import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, User, ExternalLink, Tag } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  slug: string;
  published_at: string;
  author: string;
  category: string;
  featured_image?: string;
}

const BlogSection: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      // Fallback to static content if no blog posts
      setPosts([
        {
          id: '1',
          title: 'Guide: Planera den perfekta studentfesten',
          excerpt: 'Allt du behöver veta för att skapa en minnesvärd studentfest med rätt ljud och ljus.',
          slug: 'planera-perfekta-studentfesten',
          published_at: '2024-03-15',
          author: 'Rasmus Monsen',
          category: 'Student',
          featured_image: 'https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          id: '2',
          title: 'Checklista för företagsevent',
          excerpt: 'En komplett guide för att planera professionella företagsevent med rätt teknik.',
          slug: 'checklista-foretagsevent',
          published_at: '2024-03-10',
          author: 'Rasmus Monsen',
          category: 'Företag',
          featured_image: 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          id: '3',
          title: 'Så väljer du rätt ljudutrustning',
          excerpt: 'Tips och råd för att välja rätt ljudsystem baserat på din festtyp och antal gäster.',
          slug: 'valja-ratt-ljudutrustning',
          published_at: '2024-03-05',
          author: 'Rasmus Monsen',
          category: 'Guider',
          featured_image: 'https://images.pexels.com/photos/164938/pexels-photo-164938.jpeg?auto=compress&cs=tinysrgb&w=800'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: sv });
    } catch (e) {
      return dateString;
    }
  };

  // Function to strip HTML tags from content for preview
  const stripHtmlTags = (html: string) => {
    // Create a temporary div element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Get text content and clean it up
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();
    
    return text;
  };

  // Function to get the appropriate link for each guide
  const getGuideLink = (category: string, slug: string) => {
    switch (category.toLowerCase()) {
      case 'student':
        return '/student';
      case 'företag':
      case 'foretag':
        return '/foretag';
      case 'delivery':
      case 'setup':
      case 'event':
      case 'news':
      case 'offer':
        return `/nyheter/${slug}`;
      case 'guider':
      default:
        return `/nyheter/${slug}`;
    }
  };

  const getLinkText = (category: string) => {
    switch (category.toLowerCase()) {
      case 'student':
        return 'Se studentpaket';
      case 'företag':
      case 'foretag':
        return 'Se företagspaket';
      case 'delivery':
        return 'Läs mer om leverans';
      case 'setup':
        return 'Läs mer om riggning';
      case 'event':
        return 'Läs mer om eventet';
      case 'news':
        return 'Läs hela nyheten';
      case 'offer':
        return 'Se erbjudande';
      case 'guider':
      default:
        return 'Läs mer';
    }
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, { label: string, color: string }> = {
      'student': { label: 'Student', color: 'bg-slate-800 border border-blue-500/20 text-blue-200' },
      'företag': { label: 'Företag', color: 'bg-slate-800 border border-purple-500/20 text-purple-200' },
      'foretag': { label: 'Företag', color: 'bg-slate-800 border border-purple-500/20 text-purple-200' },
      'guider': { label: 'Guide', color: 'bg-slate-800 border border-green-500/20 text-green-200' },
      'delivery': { label: 'Leverans', color: 'bg-slate-800 border border-yellow-500/20 text-yellow-200' },
      'setup': { label: 'Riggning', color: 'bg-slate-800 border border-indigo-500/20 text-indigo-200' },
      'event': { label: 'Event', color: 'bg-slate-800 border border-pink-500/20 text-pink-200' },
      'news': { label: 'Nyhet', color: 'bg-slate-800 border border-orange-500/20 text-orange-200' },
      'offer': { label: 'Erbjudande', color: 'bg-slate-800 border border-red-500/20 text-red-200' }
    };

    return categories[category.toLowerCase()] || { label: category, color: 'bg-slate-800 border border-slate-700 text-slate-200' };
  };

  if (loading) {
    return (
      <section className="py-16 md:py-20 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="blog-surface blog-surface--muted p-6 md:p-8">
          <div className="animate-pulse text-center mb-10">
            <div className="h-8 bg-slate-700 rounded w-48 mx-auto mb-2"></div>
            <div className="h-4 bg-slate-700 rounded w-72 mx-auto"></div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900/75 border border-slate-600 overflow-hidden animate-pulse">
              <div className="md:flex">
                <div className="md:w-5/12 aspect-[16/9] bg-slate-900/80 border-r border-slate-600" />
                <div className="p-6 flex-1">
                  <div className="h-4 bg-slate-700 rounded w-32 mb-4" />
                  <div className="h-6 bg-slate-700 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-slate-700 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-700 rounded w-5/6 mb-6" />
                  <div className="h-10 bg-slate-700 rounded w-44" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-slate-900/70 border border-slate-600 overflow-hidden animate-pulse md:flex"
                >
                  <div className="md:w-44 aspect-[4/3] bg-slate-900/80 border-r border-slate-600" />
                  <div className="p-5 flex-1">
                    <div className="h-3 bg-slate-700 rounded w-2/5 mb-4" />
                    <div className="h-5 bg-slate-700 rounded w-2/3 mb-3" />
                    <div className="h-3 bg-slate-700 rounded w-full mb-3" />
                    <div className="h-3 bg-slate-700 rounded w-4/5 mb-6" />
                    <div className="h-4 bg-slate-700 rounded w-28" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  const featuredPost = posts[0];
  const secondaryPosts = posts.slice(1, 3);

  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="blog-surface blog-surface--muted p-6 md:p-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Nyheter & Uppdateringar
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Håll dig uppdaterad med våra senaste nyheter, leveranser och event.
            </p>
          </div>

          <div className="space-y-6 mb-10">
            {featuredPost && (() => {
              const categoryInfo = getCategoryLabel(featuredPost.category);
              return (
                <article className="bg-slate-900/75 border border-slate-600 overflow-hidden md:flex">
                  <div className="relative md:w-5/12 aspect-[16/9] bg-slate-900/80 border-b md:border-b-0 md:border-r border-slate-600">
                    <img
                      src={featuredPost.featured_image || 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=800'}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`${categoryInfo.color} px-3 py-1 text-xs font-medium`}>
                        {categoryInfo.label}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-transparent" />
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center text-xs text-slate-500 mb-2">
                      <Calendar size={12} className="mr-1" />
                      <span>{formatDate(featuredPost.published_at)}</span>
                      <span className="mx-1">·</span>
                      <User size={12} className="mr-1" />
                      <span>{featuredPost.author}</span>
                    </div>

                    <h3 className="text-2xl font-bold mb-2 text-white line-clamp-2">
                      {featuredPost.title}
                    </h3>

                    <div
                      className="blog-excerpt text-sm mb-4 line-clamp-3 flex-grow"
                      dangerouslySetInnerHTML={{ __html: featuredPost.excerpt }}
                    />

                    <Link
                      to={getGuideLink(featuredPost.category, featuredPost.slug)}
                      className="inline-flex items-center text-purple-400 hover:text-purple-300 text-sm font-medium"
                    >
                      <span className="mr-1">{getLinkText(featuredPost.category)}</span>
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </article>
              );
            })()}

            <div className="space-y-6">
              {secondaryPosts.map((post) => {
                const categoryInfo = getCategoryLabel(post.category);
                return (
                  <article
                    key={post.id}
                    className="bg-slate-900/75 border border-slate-600 overflow-hidden flex md:items-stretch"
                  >
                    <div className="relative w-44 flex-shrink-0 bg-slate-900/80 border-r border-slate-600">
                      <div className="absolute top-3 left-3 z-10">
                        <span className={`${categoryInfo.color} px-3 py-1 text-xs font-medium`}>
                          {categoryInfo.label}
                        </span>
                      </div>
                      <img
                        src={post.featured_image || 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=800'}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <div className="p-5 flex flex-col flex-grow">
                      <div className="flex items-center text-xs text-slate-500 mb-2">
                        <Calendar size={12} className="mr-1" />
                        <span>{formatDate(post.published_at)}</span>
                        <span className="mx-1">·</span>
                        <User size={12} className="mr-1" />
                        <span>{post.author}</span>
                      </div>

                      <h3 className="text-lg font-bold mb-2 text-white line-clamp-2">
                        {post.title}
                      </h3>

                      <div
                        className="blog-excerpt text-sm mb-4 line-clamp-2 flex-grow"
                        dangerouslySetInnerHTML={{ __html: post.excerpt }}
                      />

                      <Link
                        to={getGuideLink(post.category, post.slug)}
                        className="inline-flex items-center text-purple-400 hover:text-purple-300 text-sm font-medium"
                      >
                        <span className="mr-1">{getLinkText(post.category)}</span>
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/nyheter"
              className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors"
            >
              <span className="mr-2">Se alla nyheter</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;